import { ColumnDef } from "@tanstack/react-table";
import { HeaderSortable } from "@/components/Base/HeaderSortable";
import { TechnicianStats, JobState } from "@/types/v2/DispatchView";
import { getCapacityBandClasses } from "./capacityBand";
import { formatHours } from "@/lib/formatHours";
import { Avatar } from "@/components/Common/Avatar";
import { cn } from "@/lib/utils";
import {
  JOB_STATE_TOOLTIPS,
  TECHNICIAN_COLUMN_TOOLTIP,
} from "./columnTooltips";

// HeaderSortable's base Button class defaults to text-primary-foreground
// (near-white, meant for a dark header) both at rest and on hover. This
// table's header sits on the light bg-muted/20 TableHeader background, so
// both states need an explicit override or the label washes out on hover
// even after the resting color is fixed.
const HEADER_LABEL_CLASSNAME =
  "text-xs font-semibold uppercase tracking-wide text-slate-500 hover:text-slate-500";

/**
 * Initials from a display name: first char of the first word + first char
 * of the last word, uppercased. A single-word name falls back to its own
 * first char.
 */
function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "";
  if (parts.length === 1) return parts[0][0]?.toUpperCase() ?? "";
  const first = parts[0][0] ?? "";
  const last = parts[parts.length - 1][0] ?? "";
  return `${first}${last}`.toUpperCase();
}

const HOURS_COLUMN_LABEL: Record<JobState, string> = {
  pending: "Pending",
  approved: "Approved",
  completed: "Completed",
  declined: "Declined",
};

// Amber capacity-highlight applies only to Approved hours (the "how much
// committed work is this employee still carrying" figure) — Pending/
// Completed/Declined render as plain slate text.
//
// The roster can now include employees with nothing assigned at all (a
// service advisor, a parts clerk, any non-technician when the shop shows
// every employee) — for them, zero Approved hours means "not applicable,"
// not "wide open." Only highlight when the employee has some recorded
// activity somewhere; a row with 0 in every bucket renders as plain text,
// the same as an at-or-above-8h row.
function hasAnyRecordedHours(stats: TechnicianStats): boolean {
  const { pending, approved, completed, declined } = stats.hoursByState;
  return pending > 0 || approved > 0 || completed > 0 || declined > 0;
}

function hoursColumn(state: JobState): ColumnDef<TechnicianStats> {
  return {
    id: `${state}Hours`,
    accessorFn: (row) => row.hoursByState[state],
    meta: {
      label: HOURS_COLUMN_LABEL[state],
      getCellClass: (row) => {
        const stats = row.original as TechnicianStats;
        if (state !== "approved" || !hasAnyRecordedHours(stats)) {
          return "text-right tabular-nums text-slate-600";
        }
        const band = getCapacityBandClasses(stats.hoursByState.approved);
        return cn(
          band.cell,
          band.text,
          band.bold && "font-bold",
          "text-right tabular-nums"
        );
      },
    },
    header: ({ column }) => (
      <HeaderSortable
        column={column}
        title={HOURS_COLUMN_LABEL[state]}
        labelClassName={HEADER_LABEL_CLASSNAME}
        tooltip={JOB_STATE_TOOLTIPS[state]}
      />
    ),
    sortingFn: (a, b) => {
      const diff =
        a.original.hoursByState[state] - b.original.hoursByState[state];
      return diff !== 0
        ? diff
        : a.original.technicianName.localeCompare(b.original.technicianName);
    },
    cell: ({ row }) => formatHours(row.original.hoursByState[state]),
  };
}

export const technicianHoursColumns = (): ColumnDef<TechnicianStats>[] => [
  {
    accessorKey: "technicianName",
    // The only column that says *whose* row this is, so it stays out of the
    // Columns menu -- hiding it would leave four anonymous hour figures.
    enableHiding: false,
    meta: {
      label: "Technician",
      // Capped so a long name ellipsizes instead of pushing the four hour
      // columns off the side panel, which is only ~500px wide.
      getCellClass: () => "max-w-[9rem]",
    },
    header: ({ column }) => (
      <HeaderSortable
        column={column}
        title="Technician"
        labelClassName={HEADER_LABEL_CLASSNAME}
        tooltip={TECHNICIAN_COLUMN_TOOLTIP}
      />
    ),
    sortingFn: (a, b) =>
      a.original.technicianName.localeCompare(b.original.technicianName),
    cell: ({ row }) => (
      <div className="flex min-w-0 items-center gap-2">
        <Avatar
          bgColor={row.original.technicianColor}
          fallback={getInitials(row.original.technicianName)}
          className="h-7 w-7 shrink-0 text-xs"
        />
        {/* A native title, not the Radix tooltip used on the headers: the
            whole row is a click target for the drill-down, and this only
            needs to reveal a name the ellipsis cut off. */}
        <span
          className="truncate text-sm font-medium text-slate-700"
          title={row.original.technicianName}
        >
          {row.original.technicianName}
        </span>
      </div>
    ),
  },
  hoursColumn("pending"),
  hoursColumn("approved"),
  hoursColumn("completed"),
  hoursColumn("declined"),
];
